import React, { useState, useRef } from "react";
import { FormikValues } from "formik";
import { AddPhotoAlternate, Delete, Visibility, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useUploadImageMutation } from "../../api/fileUpload";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./imageGalleryField.css";

interface ImageGalleryFieldProps {
  name: string;
  label: string;
  formik: FormikValues;
  width?: "full" | "half";
}

export const ImageGalleryField: React.FC<ImageGalleryFieldProps> = ({
  name,
  label,
  formik,
  width = "full",
}) => {
  const [images, setImages] = useState<string[]>(
    formik.values[name] || []
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadImage, { isLoading: uploadIsLoading }] = useUploadImageMutation();
  const sliderRef = useRef<Slider>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newImages: string[] = [];
      
      // Process each file
      for (const file of Array.from(files)) {
        // Check if file is an image
        if (!file.type.startsWith("image/")) {
          alert("Please select only image files");
          continue;
        }

        try {
          // Create FormData for S3 upload
          const formData = new FormData();
          formData.append("file", file);

          // Upload to S3
          const res = await uploadImage(formData).unwrap();
          if (res.data.data.url) {
            newImages.push(res.data.data.url);
          }
        } catch (error) {
          console.log("Image upload error:", error);
          alert(`Failed to upload ${file.name}. Please try again.`);
        }
      }

      if (newImages.length > 0) {
        const updatedImages = [...images, ...newImages];
        setImages(updatedImages);
        formik.setFieldValue(name, updatedImages);
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    formik.setFieldValue(name, updatedImages);
    
    // Revoke the URL to free memory
    URL.revokeObjectURL(images[index]);
  };

  const handlePreviewImage = () => {
    if (images.length > 0) {
      setIsModalOpen(true);
    }
  };

  const selectImage = (index: number) => {
    setCurrentIndex(index);
    sliderRef.current?.slickGoTo(index);
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    customPaging: (i: number) => (
      <div
        className={`w-3 h-3 mx-1 rounded-sm transition-all duration-200 ${i === currentIndex ? "bg-white" : "bg-white/30 border border-white/50"
          }`}
      />
    ),
    beforeChange: (oldIndex: number, newIndex: number) => setCurrentIndex(newIndex),
    prevArrow: <button className="absolute left-2 top-1/2 transform -translate-y-1/2 z-30 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full transition-all duration-200 shadow-lg">
      <ChevronLeft className="h-10 !text-xl w-10 text-gray-500" />
    </button>,
    nextArrow: <button className="absolute right-2 top-1/2 transform -translate-y-1/2 z-30 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full transition-all duration-200 shadow-lg">
      <ChevronRight className="h-10 w-10 text-gray-500" />
    </button>,
    autoplay: false,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          arrows: false,
          dots: false
        }
      }
    ]
  };

  return (
    <div className={`${width === "half" ? "w-[48%]" : "w-full"} mb-4`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
        <div className="text-center mb-4">
          <AddPhotoAlternate className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-2">
            <label
              htmlFor={`gallery-${name}`}
              className={`cursor-pointer px-4 py-2 rounded-md transition-colors ${
                uploadIsLoading 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {uploadIsLoading ? 'Uploading...' : 'Add Images'}
            </label>
            <input
              id={`gallery-${name}`}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
              disabled={uploadIsLoading}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Select multiple images to add to gallery
          </p>
        </div>

        {images.length > 0 && (
          <div className="space-y-4">
            {/* Main Carousel */}
            <div className="relative">
              <div className="w-full max-w-[500px] h-max bg-gray-100 rounded-lg overflow-hidden mx-auto image-gallery-field">
                <Slider ref={sliderRef} {...sliderSettings} className="h-full w-full max-w-[500px] max-h-[500px] flex items-center justify-center">
                  {images.map((image, index) => (
                    <div key={index} className="w-full h-full max-h-[500px] flex items-center justify-center relative">
                      {image?.toLowerCase().endsWith('.svg') ? (
                        <object
                          data={image}
                          type="image/svg+xml"
                          className="w-full h-full"
                        >
                          <img
                            src={image}
                            alt={`Gallery image ${index + 1}`}
                            className="w-auto h-full object-contain"
                          />
                        </object>
                      ) : (
                        <img
                          src={image}
                          alt={`Gallery image ${index + 1}`}
                          className="w-full h-full max-h-[500px] object-contain"
                        />
                      )}
                    </div>
                  ))}
                </Slider>
                
                {/* Action Buttons */}
                <div className="absolute top-2 right-2 flex space-x-2 z-20">
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(currentIndex)}
                    className="p-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    title="Remove Image"
                  >
                    <Delete className="h-4 w-6" />
                  </button>
                </div>
              </div>
            </div>

            {/* Thumbnails */}
            {/* {images.length > 1 && (
              <div className="w-full flex space-x-2 overflow-x-auto py-2 px-1">
                {images.map((imageUrl, index) => (
                  <div
                    key={index}
                    className={`w-[${80 / images.length}%] h-20 cursor-pointer border-2 rounded-lg overflow-hidden transition-all flex-shrink-0 ${
                      index === currentIndex 
                        ? 'border-blue-500 scale-105 shadow-lg' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => selectImage(index)}
                  >
                    {imageUrl?.toLowerCase().endsWith('.svg') ? (
                      <object
                        data={imageUrl}
                        type="image/svg+xml"
                        className="w-full h-20"
                      >
                        <img
                          src={imageUrl}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-20 object-cover"
                        />
                      </object>
                    ) : (
                      <img
                        src={imageUrl}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                ))}
              </div>
            )} */}

            {/* Image Counter */}
            {images.length > 1 && (
              <div className="text-center text-sm text-gray-500">
                {currentIndex + 1} of {images.length} images
              </div>
            )}
          </div>
        )}
      </div>
      
      {formik.touched[name] && formik.errors[name] && (
        <p className="text-red-500 text-xs mt-1">{formik.errors[name]}</p>
      )}
    </div>
  );
};
